import { supabase } from '../supabaseClient';
import type { AppView } from '../../types';
import { mitFrischenIds } from '../../tutorial/vorlagenLogik';
import type { UebungsVorlagenInhalt } from '../../tutorial/vorlagenLogik';
import { ANLAGE_ABGEBROCHEN_TEXT, gespeichertePlotBreite, sigePlanVorlagenFuerAnlage, vorbelegungNeuerPlan } from '../sigePlanVorlagen';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ÜBUNGSPROJEKTE — der Schreib- und Lesepfad
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Plan: docs/lernzentrum-tutorial-plan.md, Abschnitte 1.11 und 8.
 *
 * Ein Übungsprojekt ist ein ganz normales Projekt mit genau einem Unterschied:
 * es trägt `projects.ist_uebung = true` und ist damit **aus allem
 * herausgehalten, was zählt** — Zähler, Buchungsjournal, Integritätswächter,
 * KPIs, Dashboard-Fälligkeiten, Versand, Rechnungsnummernkreis, DATEV,
 * Projektkarte, Auto-Archivierung. Der Nutzer übt am echten Code, nicht an
 * einer Attrappe; nur die Auswertungen sehen ihn nicht.
 *
 * ─── Fail-open, und zwar bewusst ───────────────────────────────────────────
 *
 * Die Spalte, die Sichten und die beiden RPCs kommen mit Migration Nr. 100/101
 * (Auftrag A1) und laufen zuerst nur auf TEST. Solange sie fehlen, darf der
 * Übungsplatz nicht mit einem roten Fehler dastehen, sondern sagt „noch nicht
 * freigeschaltet" — dasselbe Muster wie `tutorialService` und wie die
 * Medien-Galerie (Nr. 99). Erkannt an `42P01` (Relation fehlt), `PGRST205`
 * (Tabelle nicht im Schema-Cache), `PGRST202`/`42883` (Funktion fehlt) und
 * `42703` (Spalte fehlt).
 *
 * ─── Die Vorlagen ──────────────────────────────────────────────────────────
 *
 * Die Vorlagen liegen als JSON unter `src/tutorial/vorlagen/`. Dieselben
 * Dateien liest später das Seed-Skript der Tutorial-Pipeline (Paket C), damit
 * Video, Screenshot und Übungsprojekt denselben Stand zeigen. Der Aufbau einer
 * Vorlage steht als Typ `UebungsVorlagenInhalt` in `src/tutorial/vorlagenLogik.ts`
 * — das ist der verbindliche Vertrag gegenüber der RPC `uebungsprojekt_anlegen`
 * und zugleich die Datei, die das Seed-Skript der Pipeline liest.
 */

/** Der Wortlaut, den Sendeschutz und Hub-Streifen teilen — nie neu erfinden. */
export const UEBUNG_KEIN_VERSAND = 'Übungsprojekt: kein Versand';

/** Was der Übungsplatz zeigt, solange Nr. 100/101 nicht gelaufen ist. */
export const UEBUNG_NICHT_FREIGESCHALTET =
    'Der Übungsplatz ist hier noch nicht freigeschaltet. Die Datenbank-Migration steht noch aus.';

/** Text des gelben Hub-Streifens (Plan 8.1, wörtlich). */
export const UEBUNG_HUB_TEXT =
    'Übungsprojekt — nichts hier zählt, wird versendet oder abgerechnet.';

/**
 * Fehlercodes, die „die Ablage gibt es noch nicht" bedeuten — und nur die.
 * Ein Netz- oder Rechtefehler gehört NICHT dazu: der muss sichtbar bleiben,
 * sonst sieht eine kaputte Berechtigung wie eine fehlende Migration aus.
 */
const FEHLENDE_ABLAGE = new Set(['42P01', 'PGRST205', 'PGRST202', '42883', '42703']);

export const istUebungsablageFehlt = (e: unknown): boolean => {
    const code = (e as { code?: unknown } | null)?.code;
    return typeof code === 'string' && FEHLENDE_ABLAGE.has(code);
};

/** Fehler des Übungsplatzes, der als „noch nicht freigeschaltet" angezeigt wird. */
export class UebungNichtFreigeschaltet extends Error {
    constructor(public readonly ursache?: unknown) {
        super(UEBUNG_NICHT_FREIGESCHALTET);
        this.name = 'UebungNichtFreigeschaltet';
    }
}

/* ═════════════════════════════════════════════════════════════════════════
 * 1. Erkennung
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Alles, woran ein Übungsprojekt zu erkennen ist. Bewusst ein eigener,
 * loser Typ statt `Pick<Project, …>`: eine Zeile aus `projects` ist in diesem
 * Repo nirgends typisiert (die Listen führen `any[]`), und dieselbe Frage
 * wird an vier verschiedenen Formen gestellt — an der DB-Zeile, am geladenen
 * `Report`, an `globalData` und an einer Projektnummer.
 */
export interface UebungsMerkmale {
    /** Die führende Quelle: Spalte `projects.ist_uebung` (Migration Nr. 100). */
    ist_uebung?: boolean | null;
    /** Projektnummer; Übungsprojekte tragen den reservierten Präfix `UEB-`. */
    project_number?: string | null;
    /**
     * DB-Zeile: der Blob.
     *
     * `globalData` steht hier bewusst als `unknown` und nicht als
     * `{ istUebung?: boolean }`: die echte `ProjectGlobalData` (types.ts) führt
     * das Kennzeichen gar nicht in ihrer Deklaration, und ein Zieltyp aus lauter
     * optionalen Feldern lehnt jedes Objekt ab, mit dem er kein einziges Feld
     * teilt (schwacher Typ). Gelesen wird das Kennzeichen über `istUebungFlagge`
     * — dieselbe Prüfung wie vorher, nur ohne die Form vorzuschreiben.
     */
    data?: { metadata?: { globalData?: unknown } | null } | null;
    /** Geladener Bericht (`Report`) statt DB-Zeile. */
    metadata?: { globalData?: unknown } | null;
    /** Direkt die `globalData` eines Berichts. */
    istUebung?: boolean | null;
}

/** Reservierter Präfix der Übungs-Projektnummern (Plan 8.1). */
export const UEBUNG_NUMMER_PRAEFIX = 'UEB-';

/** Trägt diese `globalData` das Kennzeichen? Genau `istUebung === true`, sonst nichts. */
const istUebungFlagge = (g: unknown): boolean =>
    !!g && typeof g === 'object' && (g as { istUebung?: unknown }).istUebung === true;

/**
 * Ist das ein Übungsprojekt?
 *
 * Vier Wege, und das ist Absicht. Die Spalte ist die Wahrheit, aber sie steht
 * nicht in jedem Objekt, das durch diese App wandert: die Projektliste reicht
 * DB-Zeilen durch, der Hub einen `Report`, der Sendeschutz eine `globalData`.
 * Eine Frage, die je nach Aufrufer eine andere Antwort gibt, wäre genau die
 * Klasse Fehler, gegen die dieses Modul gebaut ist — deshalb prüft die
 * Funktion alle Formen und der Blob trägt das Kennzeichen zusätzlich mit.
 *
 * DIE SPALTE IST FÜHREND, UND ZWAR AUCH MIT `false`. Vorher war jeder Weg für
 * sich hinreichend; ein echtes Projekt, dessen Blob irgendwann einmal ein
 * `istUebung: true` abbekommen hat (kopierte Vorlage, Datenübernahme,
 * Fehlgriff im Wartungswerkzeug), bekäme damit den gelben Streifen samt
 * Löschknopf ohne Vier-Augen-Prinzip — genau die Richtung, in die dieses Modul
 * nie irren darf. Liegt `ist_uebung` als boolean vor (also: wir haben eine
 * DB-Zeile in der Hand), entscheidet sie allein. Blob und Nummer sind nur der
 * Rückfall für die Formen, die die Spalte gar nicht mitführen (`Report`,
 * `globalData`).
 *
 * Reine Funktion, ohne Datenbank: so ist sie überall aufrufbar, auch mitten im
 * Render, und ohne DOM-Testumgebung prüfbar.
 */
export const istUebungsprojekt = (p?: UebungsMerkmale | null): boolean => {
    if (!p) return false;
    if (typeof p.ist_uebung === 'boolean') return p.ist_uebung;
    if (p.istUebung === true) return true;
    if (istUebungFlagge(p.data?.metadata?.globalData)) return true;
    if (istUebungFlagge(p.metadata?.globalData)) return true;
    const nummer = p.project_number;
    return typeof nummer === 'string' && nummer.startsWith(UEBUNG_NUMMER_PRAEFIX);
};

/**
 * Ist das ein PERSÖNLICHES Übungsprojekt — eines vom Übungsplatz?
 *
 * `ist_uebung` trägt zwei verschiedene Dinge. Das eine ist der Übungsplatz:
 * ein Projekt, das sich ein Nutzer selbst angelegt hat, mit der reservierten
 * Nummer `UEB-<vorlage>-<kürzel>`, das er zurücksetzen und löschen darf. Das
 * andere sind die Demo-Projekte der Tutorial-Pipeline (26099001/26099002,
 * Eigentümer `tutorial-k`): die tragen dieselbe Spalte, damit sie aus Zählern,
 * Fälligkeiten und Belegen herausfallen, sind aber die KULISSE für Video und
 * Screenshot und führen ganz normale Projektnummern.
 *
 * Für die AUSWERTUNGSABGRENZUNG bleibt `ist_uebung` das Maß — beide sollen
 * unsichtbar sein. Für alles, was im Hub SICHTBAR ist, gilt diese Funktion:
 * der gelbe Streifen samt „Zurücksetzen" und „Löschen" gehört auf kein Bild
 * der Pipeline, und ein Löschknopf an einem Demo-Projekt wäre ohnehin nur ein
 * Fehlgriff, der darauf wartet, passiert zu werden.
 */
export const istPersoenlichesUebungsprojekt = (p?: UebungsMerkmale | null): boolean => {
    if (!istUebungsprojekt(p)) return false;
    const nummer = p?.project_number;
    return typeof nummer === 'string' && nummer.startsWith(UEBUNG_NUMMER_PRAEFIX);
};

/* ═════════════════════════════════════════════════════════════════════════
 * 2. Die Vorlagen
 * ═══════════════════════════════════════════════════════════════════════ */

export interface UebungsVorlage {
    /** Schlüssel der Vorlage; zugleich Dateiname unter `src/tutorial/vorlagen/`. */
    id: string;
    /** Beschriftung der Kachel, z. B. „Test Begehungsbericht". */
    titel: string;
    /** Wohin gesprungen wird, sobald das Projekt offen ist. */
    view: AppView;
    beschreibung: string;
    /** Angekündigt, aber ohne Inhalt (Paket F) — Kachel grau, kein Klickziel. */
    geplant?: boolean;
}

/**
 * Die Kacheln des Übungsplatzes (Plan 8.1).
 *
 * v1 hat genau ZWEI vollständige Vorlagen — Begehung und SiGe-Plan. Das ist
 * keine Sparmaßnahme, sondern die Paketzuordnung aus 8.6: die beiden reichen
 * für den ersten Praxistest, die übrigen ergänzt Paket F mitsamt der Fragen,
 * die an ihnen hängen (Rechnungsnummernkreis, Lose, Freelancer-Portal). Eine
 * Kachel ohne Inhalt ist trotzdem sichtbar, damit der Nutzer sieht, was kommt,
 * und nicht nach etwas sucht, das es noch nicht gibt.
 */
export const UEBUNGS_VORLAGEN: UebungsVorlage[] = [
    {
        id: 'begehung',
        titel: 'Test Begehungsbericht',
        view: 'module-sigeko-inspection',
        beschreibung: 'Zwei abgeschlossene Begehungen, eine offene dritte mit Mängeln. Bericht schreiben, Mängel erfassen, Leistung buchen.',
    },
    {
        id: 'sigeplan',
        titel: 'Test SiGe-Plan',
        view: 'module-sige-plan',
        beschreibung: 'Plan mit drei Abschnitten und sechs Arbeitsschritten. Gefährdungen ergänzen, Plankopf füllen, Plan drucken.',
    },
    { id: 'einweisung',       titel: 'Test Einweisung',        view: 'module-sige-einweisung', beschreibung: 'Einweisung anlegen, Teilnehmer erfassen, Nachweis erzeugen.', geplant: true },
    { id: 'unterlage',        titel: 'Test Unterlage RAB 32',  view: 'module-unterlage',       beschreibung: 'Unterlage für spätere Arbeiten aufbauen und versionieren.', geplant: true },
    { id: 'vorankuendigung',  titel: 'Test Vorankündigung',    view: 'module-vorank',          beschreibung: 'Vorankündigung ausfüllen und an die Behörde melden.', geplant: true },
    { id: 'planungsphase',    titel: 'Test Planungsphase',     view: 'module-planung',         beschreibung: 'Planungsbeiträge und Koordinierung in der Planung.', geplant: true },
    { id: 'asplan',           titel: 'Test A+S-Plan',          view: 'module-asplan',          beschreibung: 'Anlauf- und Sicherungsplan mit Flächen.', geplant: true },
    { id: 'baustellenordnung', titel: 'Test Baustellenordnung', view: 'module-bauordnung',     beschreibung: 'Baustellenordnung und HSE-Vorgaben zusammenstellen.', geplant: true },
    { id: 'dokumente',        titel: 'Test Dokumente',         view: 'module-dokumente',       beschreibung: 'Ablage, Versionen und Verteiler üben.', geplant: true },
    { id: 'sifa',             titel: 'Test SiFa-Betreuung',    view: 'module-sifa-taetigkeiten', beschreibung: 'Dauerbetreuung, Fristen und Tätigkeitsnachweis.', geplant: true },
    { id: 'angebot',          titel: 'Test Angebot',           view: 'quotes',                 beschreibung: 'Angebot mit LV schreiben, nachfassen, in ein Projekt wandeln.', geplant: true },
    { id: 'rechnung',         titel: 'Test Rechnung',          view: 'invoices',               beschreibung: 'Rechnung, Teilrechnung, Storno — mit eigenem Nummernkreis UEB-.', geplant: true },
    { id: 'buchhaltung',      titel: 'Test Buchhaltung',       view: 'project-hub',            beschreibung: 'Leistungen buchen, zurücknehmen, verzichten.', geplant: true },
    { id: 'lose',             titel: 'Test Lose',              view: 'project-hub',            beschreibung: 'Mutter und Tochter verknüpfen und abrechnen.', geplant: true },
    { id: 'leer',             titel: 'Leeres Übungsprojekt',   view: 'project-hub',            beschreibung: 'Ohne Vorbefüllung — für eigene Versuche.', geplant: true },
];

export const vorlageFuerId = (id: string): UebungsVorlage | undefined =>
    UEBUNGS_VORLAGEN.find((v) => v.id === id);

/* ═════════════════════════════════════════════════════════════════════════
 * 3. Der Vertrag mit `uebungsprojekt_anlegen` (Migration Nr. 101)
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Der Vertrag der Vorlagendatei und die Ersetzung der Platzhalter-ids liegen
 * seit Paket C in `src/tutorial/vorlagenLogik.ts` — ohne `supabaseClient` und
 * ohne `import()` auf JSON, damit das Seed-Skript der Pipeline
 * (`scripts/tutorial/seed-demo.ts`) dieselbe Logik benutzen kann wie der
 * Client (Plan 8.3: „Das Seed-Skript … und der Knopf ,Selbst ausprobieren'
 * lesen DIESELBEN Dateien``).
 *
 * Hier stehen sie weiterhin als Export, damit kein Aufrufer und kein Test
 * umgeschrieben werden muss; der Vertrag ist unveraendert.
 */
export type { UebungsVorlagenInhalt };
export { mitFrischenIds };

/**
 * Lader je Vorlage. Bewusst eine ausgeschriebene Tabelle statt eines
 * dynamischen Imports mit berechnetem Pfad: den kann Vite nur über eine
 * Glob-Analyse auflösen, zieht dann ALLE Dateien des Ordners ins Bundle und
 * verliert genau den Vorteil, um den es hier geht — eine Vorlage wird erst
 * geladen, wenn jemand sie anklickt.
 */
const VORLAGEN_LADER: Record<string, () => Promise<{ default: unknown }>> = {
    begehung: () => import('../../tutorial/vorlagen/begehung.json'),
    sigeplan: () => import('../../tutorial/vorlagen/sigeplan.json'),
};

export const ladeVorlagenInhalt = async (vorlageId: string): Promise<UebungsVorlagenInhalt> => {
    const lader = VORLAGEN_LADER[vorlageId];
    if (!lader) throw new Error(`Für „${vorlageId}" gibt es noch keine Vorlage.`);
    const modul = await lader();
    // Tiefe Kopie: der Modul-Cache von Vite gibt bei jedem Aufruf DASSELBE
    // Objekt zurück; ohne Kopie trüge der zweite Anlauf die id-Ersetzungen
    // des ersten.
    return JSON.parse(JSON.stringify(modul.default ?? modul)) as UebungsVorlagenInhalt;
};


/* ═════════════════════════════════════════════════════════════════════════
 * 4. Anlegen, Zurücksetzen, Löschen
 * ═══════════════════════════════════════════════════════════════════════ */

/** Die Kindzeilen des SiGe-Plans. Fehlt eine Tabelle, wird nur gewarnt. */
const schreibeSigePlan = async (
    projektId: string,
    plan: NonNullable<UebungsVorlagenInhalt['sigeplan']>,
): Promise<void> => {
    try {
        // Die Vorlage bringt ihre Plotterbreite selbst mit (Video, Screenshot
        // und Übung zeigen denselben Stand); fehlt sie oder ist sie leer, gilt
        // wie bei jedem neuen Plan die Standardbreite der Firma — nie NULL, und
        // erst nach dem Laden der Vorlagen (sie wird dauerhaft gespeichert).
        let plotBreite = gespeichertePlotBreite(plan.projekt.plot_breite_mm);
        if (plotBreite === null) {
            const anlage = await sigePlanVorlagenFuerAnlage();
            // Sitzung gewechselt, während gewartet wurde: keinen Plan anlegen —
            // das Übungsprojekt bleibt ohne vorbefüllten Plan (siehe unten).
            if (anlage.abgebrochen === true) {
                console.warn('Übungs-SiGe-Plan nicht angelegt:', ANLAGE_ABGEBROCHEN_TEXT);
                return;
            }
            plotBreite = vorbelegungNeuerPlan(anlage.vorlagen).plot_breite_mm;
        }
        const { error } = await supabase
            .from('sige_projekte')
            .insert({
                ...plan.projekt,
                plot_breite_mm: plotBreite,
                project_id: projektId,
            });
        if (error) throw error;
        if (plan.arbeitsschritte.length) {
            const { error: e2 } = await supabase.from('sige_arbeitsschritte').insert(plan.arbeitsschritte);
            if (e2) throw e2;
        }
        if (plan.bloecke.length) {
            const { error: e3 } = await supabase.from('sige_bloecke').insert(plan.bloecke);
            if (e3) throw e3;
        }
    } catch (e) {
        // Kein Abbruch: das Projekt steht bereits, und ein Übungsprojekt ohne
        // vorbefüllten Plan ist immer noch brauchbar — der Nutzer legt ihn im
        // Assistenten selbst an. Ein Abbruch hinterliesse dagegen ein halb
        // geschriebenes Projekt und keinen Weg zurück.
        console.warn('Übungs-SiGe-Plan konnte nicht angelegt werden:', e);
    }
};

/** Die Spalten der Projektzeile, die nach der RPC den Ton angeben. */
export interface UebungsProjektStand {
    project_number?: string | null;
    name?: string | null;
}

/** Alles, was `uebernimmRpcStand` am Bericht anfasst. */
export interface UebungsBerichtKopf {
    metadata: { projectNumber?: string; projectName?: string };
}

/**
 * Übernimmt Nummer und Namen, die die RPC VERGEBEN hat, in den Bericht.
 *
 * Ohne diesen Schritt lief der Anlageweg in einen stillen Totalausfall: die
 * Vorlage trägt `metadata.projectNumber = "UEB-begehung"`, die RPC vergibt
 * aber `UEB-begehung-<kürzel>` und stellt dem Namen „ÜBUNG · " voran. Das
 * anschliessende `saveProjectToCloud` schreibt `project_number` und `name` aus
 * eben diesem Blob — und überschrieb damit beides wieder auf den
 * Vorlagenstand. Danach fand `findeMeinUebungsprojekt` (sucht nach
 * `UEB-<vorlage>-%`) das Projekt nicht mehr: jeder Klick auf die Kachel legte
 * ein weiteres an, und „Zurücksetzen" traf jedes Mal ein anderes.
 *
 * Reine Funktion auf zwei Objekten, damit die Regel ohne Datenbank prüfbar
 * ist. Sie ARBEITET AUF DEM BERICHT (kein Klon): derselbe Bericht wandert
 * gleich in `saveProjectToCloud`, eine Kopie ginge daran vorbei.
 *
 * Leere oder fehlende Werte lassen den Vorlagenstand stehen — ein Projekt
 * ohne Nummer wäre schlimmer als eines mit der Vorlagennummer.
 */
export const uebernimmRpcStand = (report: UebungsBerichtKopf, zeile?: UebungsProjektStand | null): void => {
    const nummer = typeof zeile?.project_number === 'string' ? zeile.project_number.trim() : '';
    const name = typeof zeile?.name === 'string' ? zeile.name.trim() : '';
    if (nummer) report.metadata.projectNumber = nummer;
    if (name) report.metadata.projectName = name;
};

/** Die angemeldete Nutzer-id, oder null. Wirft nie. */
const meineUserId = async (): Promise<string | null> => {
    try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id ?? null;
    } catch {
        return null;
    }
};

/**
 * Legt das Übungsprojekt zu einer Vorlage an — oder öffnet das vorhandene.
 *
 * Es gibt genau EIN Übungsprojekt je Nutzer und Vorlage; das entscheidet die
 * RPC und nicht der Client (sonst läge zwischen Nachsehen und Anlegen ein
 * Zeitfenster, und zwei schnelle Klicks erzeugten zwei Projekte). Ohne
 * `zuruecksetzen` gibt die RPC die id eines vorhandenen Projekts zurück, ohne
 * etwas zu schreiben — dann ist auch hier nichts mehr zu tun, und das ist
 * tragend: ein Nachfüllen der Kindzeilen überschriebe den geübten Stand.
 *
 * `zuruecksetzen` ist derselbe Aufruf mit demselben Inhalt: die RPC räumt die
 * Kindzeilen ab und schreibt den Blob neu, der Client füllt sie wieder — ohne
 * Rückfrage, so steht es im Plan 8.1.
 *
 * @returns die Projekt-id.
 */
export const uebungsprojektAnlegen = async (
    vorlageId: string,
    zuruecksetzen = false,
): Promise<string> => {
    const roh = await ladeVorlagenInhalt(vorlageId);
    const inhalt = mitFrischenIds(roh);

    // Vor dem Aufruf nachsehen, ob es das Projekt schon gibt. Nach dem Aufruf
    // ginge es nicht mehr: die RPC gibt in beiden Fällen dieselbe id zurück
    // und sagt nicht, ob sie geschrieben hat.
    const schonDa = zuruecksetzen ? null : await findeMeinUebungsprojekt(vorlageId);

    let projektId: string;
    try {
        const { data, error } = await supabase.rpc('uebungsprojekt_anlegen', {
            p_vorlage: vorlageId,
            p_inhalt: inhalt.report,
            p_zuruecksetzen: zuruecksetzen,
        });
        if (error) throw error;
        projektId = typeof data === 'string' ? data : String((data as { id?: string } | null)?.id ?? '');
        if (!projektId) throw new Error('Die Anlage hat keine Projekt-id geliefert.');
    } catch (e) {
        if (istUebungsablageFehlt(e)) throw new UebungNichtFreigeschaltet(e);
        throw e;
    }

    if (schonDa) return projektId;

    // Ab hier: Kindzeilen schreiben — über `saveProjectToCloud`, also über den
    // ganz normalen Speicherweg der App. Er legt LV, Beteiligte, Mängel und
    // Checklisten an und schreibt den Blob so, wie ihn `loadProjectById`
    // erwartet. Dynamisch geladen, damit der Übungsplatz nicht den halben
    // Speicherweg ins Startbundle zieht.
    const { saveProjectToCloud, saveReportSnapshot } = await import('../cloudService');

    const report = inhalt.report;
    report.metadata.globalData = {
        ...report.metadata.globalData,
        projectId: projektId,
        istUebung: true,
    } as typeof report.metadata.globalData;

    // Nummer und Name kommen von der RPC, nicht aus der Vorlage. Erst lesen,
    // dann speichern — `saveProjectToCloud` schreibt beide Spalten aus dem
    // Blob und würde den eben vergebenen Stand sonst überschreiben (siehe
    // `uebernimmRpcStand`). Scheitert das Lesen, wird trotzdem gespeichert:
    // ein Projekt mit falscher Nummer lässt sich reparieren, ein
    // abgebrochener Anlageweg hinterliesse eine Projektzeile ohne Kindzeilen.
    try {
        const { data: zeile, error: leseFehler } = await supabase
            .from('projects')
            .select('project_number, name')
            .eq('id', projektId)
            .maybeSingle();
        if (leseFehler) throw leseFehler;
        uebernimmRpcStand(report, zeile as UebungsProjektStand | null);
    } catch (e) {
        console.warn('Übungsprojekt: Nummer und Name der RPC konnten nicht gelesen werden:', e);
    }

    await saveProjectToCloud(report, projektId);

    // Die abgeschlossenen Begehungen ins Archiv. Nacheinander und nicht
    // parallel: `save_report_snapshot` ist ein Upsert auf
    // (project_id, inspection_number, section_id), und zwei gleichzeitige
    // Aufrufe wären genau die Klasse, gegen die der Eindeutigkeitsindex
    // gebaut wurde.
    for (const bericht of inhalt.archiv || []) {
        bericht.metadata.globalData = {
            ...bericht.metadata.globalData,
            projectId: projektId,
            istUebung: true,
        } as typeof bericht.metadata.globalData;
        // `report_history` führt Nummer und Namen als eigene Spalten — sonst
        // stünde im Archiv die Vorlagennummer neben der echten im Projekt.
        uebernimmRpcStand(bericht, {
            project_number: report.metadata.projectNumber,
            name: report.metadata.projectName,
        });
        try {
            await saveReportSnapshot(bericht, projektId);
        } catch (e) {
            console.warn('Übungs-Archiveintrag konnte nicht geschrieben werden:', e);
        }
    }

    if (inhalt.sigeplan) await schreibeSigePlan(projektId, inhalt.sigeplan);

    return projektId;
};

export interface UebungsRuecksetzung {
    /** Das Projekt, das die RPC zurückgesetzt hat. */
    id: string;
    /** Es ist NICHT das Projekt, das gerade offen steht. */
    andereId: boolean;
}

/**
 * Setzt das Übungsprojekt einer Vorlage auf den Vorlagenstand zurück.
 *
 * Dünne Schicht über `uebungsprojektAnlegen(…, true)` — aber sie gehört
 * hierher und nicht in den Hub (stehende Entscheidung „ProjectHub: dünner
 * Container"). Der Hub muss danach genau eine Frage beantworten können: darf
 * ich jetzt neu laden? Die Antwort hängt daran, ob die RPC dasselbe Projekt
 * getroffen hat, das offen steht. Trifft sie ein anderes — weil die
 * Projektnummer nicht mehr zur Vorlage passt oder zwei Projekte derselben
 * Vorlage existieren —, wäre ein Neuladen der schlechteste Ausgang: der Nutzer
 * landete wieder auf seinem UNVERÄNDERTEN Projekt und hielte das Zurücksetzen
 * für wirkungslos, während nebenan ein fremder Stand überschrieben wurde.
 */
export const uebungsprojektZuruecksetzen = async (
    vorlageId: string,
    offeneId?: string | null,
): Promise<UebungsRuecksetzung> => {
    const id = await uebungsprojektAnlegen(vorlageId, true);
    return { id, andereId: !!offeneId && offeneId !== 'temp-id' && id !== offeneId };
};

/**
 * Löscht ein Übungsprojekt hart.
 *
 * Ohne Vier-Augen-Prinzip und ohne Grabstein — beides gilt ausdrücklich nur
 * für echte Projekte (Plan 8.1).
 *
 * ─── Die Reihenfolge ist der ganze Punkt ───────────────────────────────────
 *
 * Vorher räumte dieser Weg die DATEIEN zuerst ab, noch bevor irgendjemand
 * geprüft hatte, ob das überhaupt ein Übungsprojekt ist. Die RPC lehnt ein
 * echtes Projekt zwar ab — aber erst danach, und die Dokumente waren dann
 * schon weg. Ein falsch verdrahteter Knopf, eine vertippte id oder ein
 * Projekt, dessen Blob fälschlich `istUebung` trägt, hätte damit die Ablage
 * eines echten Projekts geleert und wäre trotzdem „fehlgeschlagen".
 *
 * Deshalb jetzt in drei Schritten:
 *   1. Zeile lesen und prüfen: `ist_uebung === true` UND eigener Bestand.
 *      Passt etwas nicht, bricht der Weg ab, bevor irgendetwas gelöscht ist.
 *   2. Die RPC (sie prüft dasselbe ein zweites Mal, serverseitig — der Client
 *      ist hier nicht die Autorität, sondern der frühere Riegel).
 *   3. Erst wenn die RPC `true` gemeldet hat, die Dateien. Verwaiste Dateien
 *      im Bucket sind der kleinere Schaden gegenüber gelöschten Dateien eines
 *      Projekts, das stehen bleibt.
 */
export const uebungsprojektLoeschen = async (id: string): Promise<void> => {
    // 1. Prüfen — vor jedem Löschschritt.
    const uid = await meineUserId();
    if (!uid) throw new Error('Nicht angemeldet — das Übungsprojekt wurde nicht gelöscht.');

    let zeile: { ist_uebung?: boolean | null; user_id?: string | null } | null;
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('ist_uebung, user_id')
            .eq('id', id)
            .maybeSingle();
        if (error) throw error;
        zeile = data as { ist_uebung?: boolean | null; user_id?: string | null } | null;
    } catch (e) {
        if (istUebungsablageFehlt(e)) throw new UebungNichtFreigeschaltet(e);
        throw e;
    }

    // Nicht auffindbar: entweder schon gelöscht oder von der RLS verborgen.
    // In beiden Fällen ist hier nichts mehr zu tun — und nichts abzuräumen.
    if (!zeile) return;

    if (zeile.ist_uebung !== true) {
        throw new Error('Das ist kein Übungsprojekt — es wurde nichts gelöscht.');
    }
    if (zeile.user_id !== uid) {
        throw new Error('Dieses Übungsprojekt gehört einem anderen Konto — es wurde nichts gelöscht.');
    }

    // 2. Die RPC. `false` heisst „war schon weg" (siehe Nr. 101); dann bleiben
    //    die Dateien stehen, weil niemand mehr sagen kann, wem sie gehörten.
    let geloescht = false;
    try {
        const { data, error } = await supabase.rpc('uebungsprojekt_loeschen', { p_id: id });
        if (error) throw error;
        geloescht = data === true;
    } catch (e) {
        if (istUebungsablageFehlt(e)) throw new UebungNichtFreigeschaltet(e);
        throw e;
    }
    if (!geloescht) return;

    // 3. Die Dateien.
    try {
        const { loescheAlleProjektDokumente } = await import('../projektDokumenteService');
        await loescheAlleProjektDokumente(id);
    } catch (e) {
        // Kein Abbruch: eine übrig gebliebene Übungsdatei ist ein
        // Schönheitsfehler, ein rot gemeldetes Löschen eines Projekts, das
        // tatsächlich weg ist, wäre einer, den der Nutzer meldet.
        console.warn('Dateien des Übungsprojekts konnten nicht abgeräumt werden:', e);
    }
};

/**
 * Das eigene Übungsprojekt zu einer Vorlage, falls es schon eines gibt.
 * Erkennungsmerkmal ist die Projektnummer `UEB-<vorlage>-…` — dieselbe Regel,
 * nach der die RPC sucht.
 *
 * `user_id` wird AUSDRÜCKLICH mitgefiltert und nicht der RLS überlassen: eine
 * Verwaltungsrolle sieht über die Richtlinien den ganzen Bestand, und dann
 * gäbe diese Abfrage das Übungsprojekt eines fremden Kontos als „meines"
 * zurück — mit Öffnen, Zurücksetzen und Löschen daran. Ohne Sitzung kein
 * Treffer.
 */
export const findeMeinUebungsprojekt = async (vorlageId: string): Promise<string | null> => {
    const uid = await meineUserId();
    if (!uid) return null;
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('id')
            .eq('ist_uebung', true)
            .eq('user_id', uid)
            .like('project_number', `${UEBUNG_NUMMER_PRAEFIX}${vorlageId}-%`)
            .limit(1)
            .maybeSingle();
        if (error) throw error;
        return (data as { id?: string } | null)?.id ?? null;
    } catch (e) {
        if (istUebungsablageFehlt(e)) throw new UebungNichtFreigeschaltet(e);
        throw e;
    }
};

export interface MeinUebungsprojekt {
    id: string;
    name: string;
    project_number: string | null;
    /** Aus der Projektnummer `UEB-<vorlage>-<kürzel>` gelesen, soweit möglich. */
    vorlage: string | null;
    updated_at: string | null;
}

/**
 * Die eigenen Übungsprojekte.
 *
 * Liest die TABELLE `projects` und nicht die Sicht `projects_echt` — das ist
 * hier kein Versehen, sondern der Zweck: `projects_echt` blendet genau diese
 * Zeilen aus.
 *
 * `user_id` steht ausdrücklich in der Abfrage. Sich allein auf die RLS zu
 * verlassen ginge für die meisten Konten gut und für die Verwaltung schief:
 * deren Richtlinie öffnet den ganzen Bestand, und der Übungsplatz zeigte dann
 * fremde Übungsprojekte als die eigenen an. Ohne Sitzung: leere Liste.
 */
export const ladeMeineUebungsprojekte = async (): Promise<MeinUebungsprojekt[]> => {
    const uid = await meineUserId();
    if (!uid) return [];
    const { data, error } = await supabase
        .from('projects')
        .select('id, name, project_number, updated_at')
        .eq('ist_uebung', true)
        .eq('user_id', uid)
        .order('updated_at', { ascending: false });
    if (error) {
        if (istUebungsablageFehlt(error)) throw new UebungNichtFreigeschaltet(error);
        throw error;
    }
    return (data || []).map((z) => ({
        id: z.id as string,
        name: (z.name as string) || 'Übungsprojekt',
        project_number: (z.project_number as string) ?? null,
        vorlage: vorlageAusNummer(z.project_number as string | null),
        updated_at: (z.updated_at as string) ?? null,
    }));
};

/** `UEB-begehung-ab12` → `begehung`. Null, wenn die Nummer anders gebaut ist. */
export const vorlageAusNummer = (nummer?: string | null): string | null => {
    if (!nummer || !nummer.startsWith(UEBUNG_NUMMER_PRAEFIX)) return null;
    const teil = nummer.slice(UEBUNG_NUMMER_PRAEFIX.length).split('-')[0];
    return teil ? teil : null;
};

/**
 * Die ids aller Übungsprojekte — für Auswertungen, die keine Projektzeilen
 * lesen, sondern Fremdzeilen mit `project_id` (Sendeverlauf, Befundtabelle
 * des Integritätswächters).
 *
 * LEERE MENGE BEI JEDEM FEHLER, und damit bleibt alles stehen. Die sichere
 * Richtung ist hier eine Zeile zu viel: eine Übungszeile im Sendeverlauf ist
 * ein Schönheitsfehler, eine verschwiegene echte Zeile ist ein Vorfall
 * (dieselbe Begründung wie an `ladeStilleProjekte`).
 */
export const ladeUebungsProjektIds = async (): Promise<Set<string>> => {
    try {
        const { data, error } = await supabase
            .from('projects')
            .select('id')
            .eq('ist_uebung', true);
        if (error) throw error;
        return new Set((data || []).map((z) => z.id as string));
    } catch (e) {
        if (!istUebungsablageFehlt(e)) {
            console.warn('Übungsprojekte konnten nicht ermittelt werden — es wird nichts ausgeblendet:', e);
        }
        return new Set<string>();
    }
};
